import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Car, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/** "Personal Vehicle" option under a manager's clock (opt-in per user via
 *  Admin → Permissions). Logging it adds +2 paid hours to today — the
 *  manager must say WHERE they're going and WHAT they're doing. */
export default function PersonalVehicleOption({ enabled, onLogged }) {
  const [info, setInfo] = useState(null); // { hours, entries }
  const [open, setOpen] = useState(false);
  const [dest, setDest] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/personal-vehicle").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { if (enabled) load(); }, [enabled]);

  if (!enabled || !info) return null;
  const todays = info.entries?.[0];

  const submit = async () => {
    if (dest.trim().length < 2) { toast.error("Where are you going?"); return; }
    if (purpose.trim().length < 2) { toast.error("What are you doing there?"); return; }
    setBusy(true);
    try {
      await api.post("/me/personal-vehicle", { destination: dest.trim(), purpose: purpose.trim() });
      toast.success(`Personal vehicle logged — +${info.hours} hours added to today`);
      setOpen(false); setDest(""); setPurpose("");
      await load(); onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log personal vehicle");
    } finally { setBusy(false); }
  };

  const undo = async () => {
    if (!todays) return;
    setBusy(true);
    try {
      await api.delete(`/me/personal-vehicle/${todays.entry_id}`);
      toast.success("Personal vehicle removed for today");
      await load(); onLogged?.();
    } catch { toast.error("Could not remove it"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid="personal-vehicle-option">
      {todays ? (
        <div className="flex items-center gap-2 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-xs text-white/80" data-testid="personal-vehicle-logged">
          <Car className="h-4 w-4 shrink-0 text-violet-300" />
          <span className="min-w-0 flex-1 truncate">
            <b className="text-white">Personal vehicle · +{info.hours}h</b> — {todays.destination}: {todays.purpose}
          </span>
          <button type="button" onClick={undo} disabled={busy} data-testid="personal-vehicle-undo" className="shrink-0 rounded border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-white disabled:opacity-50">Undo</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="personal-vehicle-btn"
          className="flex w-full items-center justify-center gap-2 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-xs font-semibold text-violet-200 hover:bg-violet-500/20"
        >
          <Car className="h-4 w-4" /> Personal Vehicle <span className="font-mono text-[10px] text-violet-300/80">+{info.hours}h today</span>
        </button>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/75 p-3" data-testid="personal-vehicle-dialog" onClick={() => !busy && setOpen(false)}>
          <div className="w-full max-w-sm overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md border border-violet-500/40 bg-violet-500/10 text-violet-300"><Car className="h-4 w-4" /></div>
                <h3 className="text-sm font-bold uppercase tracking-tight">Personal vehicle</h3>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="personal-vehicle-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 px-4 py-4">
              <p className="text-xs text-white/60">Using your own vehicle for work today adds <b className="text-white">+{info.hours} paid hours</b> to this day. Tell us where and why.</p>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">Where are you going?</span>
                <input value={dest} onChange={(e) => setDest(e.target.value)} autoFocus maxLength={200} placeholder="e.g. Supplier pickup — Crossroads, then the Lakeshore site"
                  data-testid="personal-vehicle-destination"
                  className="mt-1 h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-[#1a3fd1] focus:outline-none" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">What are you doing?</span>
                <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3} maxLength={500} placeholder="e.g. Picking up duct wrap and dropping tools to the crew"
                  data-testid="personal-vehicle-purpose"
                  className="mt-1 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#1a3fd1] focus:outline-none" />
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
              <button onClick={() => setOpen(false)} disabled={busy} data-testid="personal-vehicle-cancel" className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold text-white/70">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="personal-vehicle-submit" className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-60">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Log +{info.hours}h
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
